import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "../../utils/axiosInstance";

export default function RoleCreate() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [permissionGroups, setPermissionGroups] = useState([]); // [{_id, name, permissions: [{_id, name, actions: [{_id, name}]}]}]
  const [formValues, setFormValues] = useState({
    roleName: "",
    description: "",
    permissions: {}, // { [groupId]: { [permissionId]: { [actionId]: boolean, selectAll: boolean } } }
    status: "Active"
  });
  const [initialRole, setInitialRole] = useState(null);

  // Fetch permission groups/entities and actions
  useEffect(() => {
    async function fetchAll() {
      let url = `/admin/api/permissionGroup?type=Admin`;
      const groupRes = await axios.get(url);
      setPermissionGroups(groupRes.data.groups || []);
    }
    fetchAll();
  }, []);

  // Build initial permissions matrix (after permissionGroups loaded)
  useEffect(() => {
    if (permissionGroups.length) {
      let matrix = {};
      permissionGroups.forEach(group => {
        matrix[group._id] = {};
        (group.permissions || []).forEach(permission => {
          matrix[group._id][permission._id] = {};
          (permission.actions || []).forEach(action => {
            matrix[group._id][permission._id][action._id] = false;
          });
          matrix[group._id][permission._id].selectAll = false;
        });
      });
      setFormValues(f => ({
        ...f,
        permissions: matrix,
      }));
    }
  }, [permissionGroups]);

  // Fetch role for edit
  useEffect(() => {
    if (id) {
      setLoading(true);
      // Always use the dynamic id from the URL param for GET and PUT
      axios.get(`/admin/api/role/${id}`)
        .then(res => {
          setInitialRole(res.data.role);
        })
        .finally(() => setLoading(false));
    }
  }, [id]);

  // Populate form for edit
  useEffect(() => {
    if (initialRole && permissionGroups.length) {
      let matrix = {};
      permissionGroups.forEach(group => {
        matrix[group._id] = {};
        (group.permissions || []).forEach(permission => {
          // Find group in initialRole.permissionGroups by _id
          let foundGroup = Array.isArray(initialRole.permissionGroups)
            ? initialRole.permissionGroups.find(
              g => (g._id || g.group?._id || g.group) === group._id
            )
            : undefined;
          // Find permission in foundGroup.permissions by _id
          let foundPerm = foundGroup && Array.isArray(foundGroup.permissions)
            ? foundGroup.permissions.find(
              p => (p._id || p.permission?._id || p.permission) === permission._id
            )
            : undefined;
          matrix[group._id][permission._id] = {};
          (permission.actions || []).forEach(action => {
            // Find action in foundPerm.actions by _id
            let foundAction = foundPerm && Array.isArray(foundPerm.actions)
              ? foundPerm.actions.find(
                a => (a._id || a.action?._id || a.action) === action._id
              )
              : undefined;
            // Use isSelected from API, default to false
            matrix[group._id][permission._id][action._id] = !!(foundAction && foundAction.isSelected);
          });
          matrix[group._id][permission._id].selectAll =
            (permission.actions || []).length > 0 &&
            (permission.actions || []).every(action => matrix[group._id][permission._id][action._id]);
        });
      });
      setFormValues({
        roleName: initialRole.name || "",
        description: initialRole.description || "",
        permissions: matrix,
        status: initialRole.status || "Active"
      });
    }
  }, [initialRole, permissionGroups]);

  // Permission change handlers
  const handlePermissionChange = (groupId, permissionId, actionId, checked) => {
    const updated = { ...formValues.permissions };
    updated[groupId][permissionId][actionId] = checked;
    // Update selectAll for this permission row
    const allChecked = Object.keys(updated[groupId][permissionId])
      .filter(k => k !== "selectAll")
      .every(aid => updated[groupId][permissionId][aid]);
    updated[groupId][permissionId].selectAll = allChecked;
    setFormValues({ ...formValues, permissions: updated });
  };

  const handleRowSelectAll = (groupId, permissionId, checked) => {
    const updated = { ...formValues.permissions };
    Object.keys(updated[groupId][permissionId]).forEach(aid => {
      if (aid !== "selectAll") updated[groupId][permissionId][aid] = checked;
    });
    updated[groupId][permissionId].selectAll = checked;
    setFormValues({ ...formValues, permissions: updated });
  };

  const handleGlobalSelectAll = (checked) => {
    let updated = { ...formValues.permissions };
    permissionGroups.forEach(group => {
      (group.permissions || []).forEach(permission => {
        Object.keys(updated[group._id][permission._id]).forEach(aid => {
          if (aid !== "selectAll") updated[group._id][permission._id][aid] = checked;
        });
        updated[group._id][permission._id].selectAll = checked;
      });
    });
    setFormValues({ ...formValues, permissions: updated });
  };

  // Check if all rows are checked (global select all)
  const isGlobalAllChecked = permissionGroups.length > 0 && permissionGroups.every(
    group => (group.permissions || []).every(
      permission => formValues.permissions[group._id]?.[permission._id]?.selectAll
    )
  );

  // Render permission rows for a group
  // Return unique actions for a group (union of all permission.actions)
  const getGroupActions = (group) => {
    const map = new Map();
    (group.permissions || []).forEach(permission => {
      (permission.actions || []).forEach(action => {
        if (!action || !action._id) return;
        if (!map.has(action._id)) map.set(action._id, action);
      });
    });
    return Array.from(map.values());
  };

  const renderPermissionRows = (group) => {
    const actions = getGroupActions(group);
    return (group.permissions || []).map(permission => (
      <tr key={permission._id}>
        <td><h6>{permission.name}</h6></td>
       
        {actions.map(act => {
          const hasAct = !!((permission.actions || []).find(a => a._id === act._id));
          return (
            <td key={act._id} className="text-center">
              <input
                type="checkbox"
                disabled={!hasAct}
                checked={formValues.permissions[group._id]?.[permission._id]?.[act._id] || false}
                onChange={hasAct ? (e => handlePermissionChange(group._id, permission._id, act._id, e.target.checked)) : undefined}
                style={{ width: 22, height: 22 }}
              />
            </td>
          );
        })}
        <td className="text-center">
          <input
            type="checkbox"
            checked={formValues.permissions[group._id]?.[permission._id]?.selectAll || false}
            onChange={e => handleRowSelectAll(group._id, permission._id, e.target.checked)}
            style={{ width: 22, height: 22 }}
          />
        </td>
      </tr>
    ));
  };

  // Helper: find actions by group name (returns array of action objects)
  const getActionsByGroupName = (groupName) => {
    if (!groupName) return [];
    const group = permissionGroups.find(g => g.name === groupName || (g.group && g.group.name) === groupName);
    return group ? getGroupActions(group) : [];
  };

  // Helper: find actions by group id
  const getActionsByGroupId = (groupId) => {
    if (!groupId) return [];
    const group = permissionGroups.find(g => g._id === groupId || g.group === groupId || (g.group && g.group._id) === groupId);
    return group ? getGroupActions(group) : [];
  };

  // Helper: return action names (strings) by group name
  const getActionNamesByGroupName = (groupName) => {
    return getActionsByGroupName(groupName).map(a => a.name).filter(Boolean);
  };

  // Helper: return action names (strings) by group id
  const getActionNamesByGroupId = (groupId) => {
    return getActionsByGroupId(groupId).map(a => a.name).filter(Boolean);
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Build payload for API
    const permissionGroupsPayload = permissionGroups.map(group => ({
      group: group._id,
      permissions: (group.permissions || []).map(permission => {
        const allowedActions = Object.keys(formValues.permissions[group._id][permission._id] || {})
          .filter(aid => aid !== "selectAll" && formValues.permissions[group._id][permission._id][aid]);
        return {
          permission: permission._id,
          allowedActions
        };
      }).filter(p => p.allowedActions.length > 0)
    })).filter(pg => pg.permissions.length > 0);

    // Get business id from localStorage
    const businessId = localStorage.getItem("businessId") || "";

    const payload = {
      name: formValues.roleName,
      description: formValues.description,
      permissionGroups: permissionGroupsPayload,
      status: formValues.status,
      business: businessId // add business id to payload
    };
    // Add auditing fields from localStorage user id and timestamps
    const userId = localStorage.getItem("userId") || localStorage.getItem("user") || "";
    const now = new Date().toISOString();
    if (id) {
      // editing existing role
      payload.updatedBy = userId;
      payload.updatedAt = now;
    } else {
      // creating new role
      payload.addedBy = userId;
      payload.addedAt = now;
    }

    try {
      if (id) {
        await axios.put(`/admin/api/role/${id}`, payload);
      } else {
        await axios.post("/admin/api/role", payload);
      }
      navigate("/user/userRole?tab=role");
    } catch (err) {
      alert("Failed to save role");
    }
  };

  if (id && loading) return <div>Loading...</div>;

  return (
    <div className="container mt-4">
      <div className="mb-4">
        <h2 className="fw-bold">{id ? "Edit Role" : "New Role"}</h2>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="card shadow-sm p-4 mb-4">
          <div className="row mb-3">
            <div className="col-md-4">
              <label className="form-label fw-semibold">Role Name</label>
              <input
                className="form-control"
                value={formValues.roleName}
                onChange={e => setFormValues({ ...formValues, roleName: e.target.value })}
                required
                placeholder="Role Name"
              />
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold">Description</label>
              <textarea
                className="form-control"
                value={formValues.description}
                onChange={e => setFormValues({ ...formValues, description: e.target.value })}
                rows={1}
                placeholder="Description"
              />
            </div>
          </div>
        </div>
        {/* Permissions Table */}
        <div className="card shadow-sm p-4 mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            {/* Left side */}
            <div>
              <span className="fw-semibold"><h5>Permissions</h5></span>
            </div>

            {/* Right side */}
            <div className="d-flex align-items-center">
              <span className="me-2 fw-semibold">Select All</span>
              <input
                type="checkbox"
                checked={isGlobalAllChecked}
                onChange={e => handleGlobalSelectAll(e.target.checked)}
                style={{ width: 22, height: 22 }}
              />
            </div>
          </div>
          {permissionGroups.map((group) => (
            <div key={group._id} className="mb-4 border rounded-3 p-3 bg-white">
              <div className="fw-bold mb-2"><h5>{group.name}</h5></div>
              <table className="table table-bordered mb-0 rounded-3">
                <thead>
                  <tr>
                    <th
                    style={{
                      backgroundColor: '#D5D8E7',
                      color: '#667085',
                      border: '2px #000',
                      borderRadius:'2px',
                    }}
                    >Module</th>
                    {(group.permissions && group.permissions.length > 0) &&
                      getGroupActions(group).map(act => (
                        <th key={act._id} className="text-center"
                        style={{
                        backgroundColor: '#D5D8E7',
                        color: '#667085',
                        border: '2px #000',
                    }}
                    >{act.name}</th>
                      ))}
                    <th className="text-center"
                    style={{
                      backgroundColor: '#D5D8E7',
                      color: '#667085',
                      border: '2px #000',
                    }}
                    >Select All</th>
                  </tr>
                </thead>
                <tbody>
                  {renderPermissionRows(group)}
                </tbody>
              </table>
            </div>
          ))}
        </div>
        <div className="d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-secondary px-4"
            onClick={() => navigate("/user/userRole")}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary px-4">
            Save
          </button>
        </div>
      </form>
    </div>
  );
}